/** One-line summary of a walking route (#473): "~48 m · about 1 min · 2 flights of stairs".
 *
 *  The distance already counts each stair or lift ride at its walking
 *  equivalent (the backend's `total_distance_meters`); the time is the
 *  backend's `estimated_seconds`; the rides come from `floor_changes`. An older
 *  backend without those fields gets just the distance. */
import type { NavigateFloorChange, NavigateResponse } from "@/lib/api/client";

type RouteTotals = Pick<NavigateResponse, "total_distance_meters" | "estimated_seconds" | "floor_changes">;

export function formatRouteDistance(meters: number): string {
  return `~${Math.round(meters)} m`;
}

/** "under a minute" / "about 1 min" / "about 4 min"; null when unknown. */
export function formatRouteTime(seconds: number | null | undefined): string | null {
  if (seconds == null || !Number.isFinite(seconds) || seconds <= 0) return null;
  if (seconds < 45) return "under a minute";
  return `about ${Math.max(1, Math.round(seconds / 60))} min`;
}

/** "2 flights of stairs", "elevator 5 floors"; both when a route takes both. */
export function formatFloorChanges(changes: NavigateFloorChange[] | null | undefined): string | null {
  if (!changes?.length) return null;
  let flights = 0;
  let liftFloors = 0;
  for (const change of changes) {
    const floors = Math.max(1, change.floors || 0);
    if (change.type === "elevator") liftFloors += floors;
    else flights += floors;
  }
  const parts: string[] = [];
  if (flights) parts.push(`${flights} ${flights === 1 ? "flight" : "flights"} of stairs`);
  if (liftFloors) parts.push(`elevator ${liftFloors} ${liftFloors === 1 ? "floor" : "floors"}`);
  return parts.join(", ");
}

/** Distance · time · floor changes, leaving out what is unknown or absent. */
export function formatRouteSummary(result: RouteTotals, options: { floorChanges?: boolean } = {}): string {
  const parts = [formatRouteDistance(result.total_distance_meters), formatRouteTime(result.estimated_seconds)];
  if (options.floorChanges !== false) parts.push(formatFloorChanges(result.floor_changes));
  return parts.filter(Boolean).join(" · ");
}
